import type jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
  PDFQuoteData,
  buildPalette,
  loadLogo,
  getJsPdfImageFormat,
  resolveDocumentTitle,
  renderDocumentSubtitle,
  fitHeaderTitle,
  formatQuoteDate,
  calcValidityDate,
  buildItemRows,
  formatCurrency,
  renderDiagnosisSection,
  renderServiceOrderSections,
  renderPaymentSection,
  renderExtraFields,
  renderPixSection,
  renderPhotosSection,
  renderSignatureSection,
  renderFooter,
  detectDocumentKind,
  formatDocument,
  pdfQuoteTotals,
  renderTotalsBreakdown,
  renderLogoWatermark,
} from './shared';

/** Cinzas próprios do template — a identidade dele é ser neutro. */
const GRAY_BOX: [number, number, number] = [243, 244, 246];
const GRAY_HEAD: [number, number, number] = [217, 219, 223];
const GRAY_DARK: [number, number, number] = [55, 65, 81];

/**
 * "Técnico" template — neutro em cinza, pensado para construção/reforma e
 * assistência técnica. Título à esquerda com filete na cor da marca, dados da
 * empresa à direita, cliente em caixa cinza arredondada e, após a tabela,
 * OBSERVAÇÕES e TOTAL lado a lado (a marca visual do layout).
 */
export async function render(doc: jsPDF, data: PDFQuoteData): Promise<void> {
  const pageWidth = doc.internal.pageSize.getWidth();

  // Marca-d'agua da logo: PRIMEIRA instrucao de desenho da pagina.
  // jsPDF pinta na ordem das chamadas e nao tem z-index, entao desenhada
  // depois ela cobriria a tabela e o total em vez de ficar atras deles.
  await renderLogoWatermark(doc, data);
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 15;
  const contentWidth = pageWidth - margin * 2;
  const { primaryColor, grayLight, grayText, darkText, white } =
    buildPalette(data.brandColorRGB);

  const logoBase64 = await loadLogo(data.company?.logoUrl);

  // === 1. CABEÇALHO: logo + título à esquerda, empresa à direita ===
  let yPos = 16;
  let titleX = margin;

  if (logoBase64) {
    try {
      const logoSize = 20;
      const format = getJsPdfImageFormat(logoBase64);
      doc.addImage(logoBase64, format, margin, yPos - 6, logoSize, logoSize);
      titleX = margin + logoSize + 5;
    } catch {
      /* segue sem logo */
    }
  }

  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...GRAY_DARK);
  // A empresa e impressa a direita na MESMA linha, limitada a metade da pagina;
  // o título vai do titleX (que já desviou da logo) até essa metade.
  const tituloCabecalho = fitHeaderTitle(
    doc,
    resolveDocumentTitle(data),
    (data.company?.name ? pageWidth / 2 : pageWidth - margin) - titleX - 4,
    19,
  );
  doc.setFontSize(tituloCabecalho.size);
  doc.text(tituloCabecalho.text, titleX, yPos + 4);
  // Subtitulo: 0 de avanco quando nao ha (o PDF do historico nao muda).
  yPos += renderDocumentSubtitle(doc, data, {
    x: titleX, y: yPos + 4,
    maxWidth: (data.company?.name ? pageWidth / 2 : pageWidth - margin) - titleX - 4,
    color: grayText, size: 8,
  });

  // Filete na cor da marca sob o título — o único acento colorido do cabeçalho.
  doc.setDrawColor(...primaryColor);
  doc.setLineWidth(1);
  doc.line(titleX, yPos + 8, titleX + 34, yPos + 8);

  if (data.company?.name) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(...GRAY_DARK);
    const nameLine = doc.splitTextToSize(
      data.company.name.toUpperCase(),
      pageWidth / 2 - margin,
    )[0];
    doc.text(nameLine, pageWidth - margin, yPos - 2, { align: 'right' });

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    doc.setTextColor(...grayText);
    let infoY = yPos + 2.5;
    const infoLines: string[] = [];
    if (data.company.cnpj) infoLines.push(`CNPJ ${data.company.cnpj}`);
    if (data.company.address) {
      infoLines.push(doc.splitTextToSize(data.company.address, pageWidth / 2 - margin)[0]);
    }
    const contact: string[] = [];
    if (data.company.phone) contact.push(data.company.phone);
    if (data.company.email) contact.push(data.company.email);
    if (contact.length) infoLines.push(contact.join(' | '));
    for (const line of infoLines) {
      doc.text(line, pageWidth - margin, infoY, { align: 'right' });
      infoY += 3.6;
    }
  }

  yPos += 18;
  doc.setDrawColor(...grayLight);
  doc.setLineWidth(0.3);
  doc.line(margin, yPos, pageWidth - margin, yPos);
  yPos += 6;

  // === 2. CLIENTE (caixa cinza arredondada) ===
  interface Row {
    label: string;
    value: string;
    rightLabel?: string;
    rightValue?: string;
  }
  const rows: Row[] = [
    {
      label: 'Nome:',
      value: data.client.name || 'Não informado',
      rightLabel: 'Data:',
      rightValue: formatQuoteDate(data.quoteDate),
    },
  ];
  if (data.client.phone) rows.push({ label: 'Telefone:', value: data.client.phone });
  if (data.client.document) {
    rows.push({
      label: detectDocumentKind(data.client.document) === 'cnpj' ? 'CNPJ:' : 'CPF:',
      value: formatDocument(data.client.document),
    });
  }
  if (data.client.address) {
    rows.push({
      label: 'Endereço:',
      value: doc.splitTextToSize(data.client.address, contentWidth - 40)[0],
    });
  }
  if (data.validityDays && data.quoteDate) {
    rows.push({
      label: 'Validade:',
      value: `até ${calcValidityDate(data.quoteDate, data.validityDays)}`,
    });
  }
  if (data.reference) {
    rows.push({
      label: 'Referência:',
      value: doc.splitTextToSize(data.reference, contentWidth - 40)[0],
    });
  }

  const rowHeight = 7.5;
  const boxHeight = rows.length * rowHeight + 4;
  doc.setFillColor(...GRAY_BOX);
  doc.roundedRect(margin, yPos, contentWidth, boxHeight, 3, 3, 'F');

  let textY = yPos + 7;
  for (const row of rows) {
    doc.setFontSize(8.5);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...grayText);
    doc.text(row.label, margin + 5, textY);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...darkText);
    doc.text(row.value, margin + 27, textY);
    if (row.rightLabel && row.rightValue) {
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...grayText);
      doc.text(row.rightLabel, pageWidth - margin - 40, textY);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...darkText);
      doc.text(row.rightValue, pageWidth - margin - 5, textY, { align: 'right' });
    }
    textY += rowHeight;
  }
  yPos += boxHeight + 7;

  // === 3. TABELA DE ITENS (cabeçalho cinza, grade fina) ===
  const { hasAnyDiscount, headers, body } = buildItemRows(data.items);
  const columnStyles = hasAnyDiscount
    ? {
        0: { cellWidth: 'auto' as const, halign: 'left' as const },
        1: { cellWidth: 15, halign: 'center' as const },
        2: { cellWidth: 13, halign: 'center' as const },
        3: { cellWidth: 27, halign: 'right' as const },
        4: { cellWidth: 20, halign: 'center' as const },
        5: { cellWidth: 29, halign: 'right' as const },
      }
    : {
        0: { cellWidth: 'auto' as const, halign: 'left' as const },
        1: { cellWidth: 18, halign: 'center' as const },
        2: { cellWidth: 13, halign: 'center' as const },
        3: { cellWidth: 30, halign: 'right' as const },
        4: { cellWidth: 30, halign: 'right' as const },
      };

  autoTable(doc, {
    startY: yPos,
    head: headers,
    body,
    theme: 'grid',
    headStyles: {
      fillColor: GRAY_HEAD,
      textColor: GRAY_DARK,
      fontStyle: 'bold',
      fontSize: 8.5,
      halign: 'center',
      cellPadding: 3.5,
      lineColor: [180, 183, 189] as [number, number, number],
      lineWidth: 0.3,
    },
    bodyStyles: {
      fontSize: 8.5,
      cellPadding: 3,
      lineColor: [205, 208, 213] as [number, number, number],
      lineWidth: 0.25,
    },
    columnStyles,
    margin: { left: margin, right: margin },
  });

  const tableEndY = (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY;
  yPos = tableEndY + 6;

  // === 4. OBSERVAÇÕES (esq.) + TOTAL (dir.), lado a lado ===
  const totals = pdfQuoteTotals(data);
  const totalColWidth = 74;
  const obsWidth = contentWidth - totalColWidth - 8;

  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  const obsLines: string[] = [];
  if (data.validityDays && data.quoteDate) {
    obsLines.push(`Validade: até ${calcValidityDate(data.quoteDate, data.validityDays)}.`);
  }
  if (data.warranty) {
    obsLines.push(...(doc.splitTextToSize(`Garantia: ${data.warranty}`, obsWidth - 10) as string[]));
  }
  if (data.notes) {
    obsLines.push(...(doc.splitTextToSize(data.notes, obsWidth - 10) as string[]));
  }
  const hasObs = obsLines.length > 0;
  const obsHeight = hasObs ? obsLines.length * 4 + 13 : 0;

  // Estimativa conservadora da coluna do total (4 linhas de decomposição + a
  // faixa) para decidir a quebra de página ANTES de desenhar qualquer metade.
  const estTotalsHeight = 21 + 15;
  if (yPos + Math.max(obsHeight, estTotalsHeight) > pageHeight - 35) {
    doc.addPage();
    yPos = 20;
  }
  const blockTop = yPos;

  if (hasObs) {
    doc.setFillColor(...GRAY_BOX);
    doc.roundedRect(margin, blockTop, obsWidth, obsHeight, 2, 2, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8.5);
    doc.setTextColor(...GRAY_DARK);
    doc.text('OBSERVAÇÕES', margin + 4, blockTop + 6);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(...grayText);
    doc.text(obsLines, margin + 4, blockTop + 11.5);
  }

  let totalY = renderTotalsBreakdown(doc, totals, blockTop, {
    margin,
    pageWidth,
    grayText,
    width: totalColWidth,
  });
  if (totalY === blockTop) totalY += 2; // sem decomposição, alinha com a caixa

  const totalBoxX = pageWidth - margin - totalColWidth;
  doc.setFillColor(...GRAY_DARK);
  doc.roundedRect(totalBoxX, totalY, totalColWidth, 13, 1.5, 1.5, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(...white);
  doc.text('TOTAL R$', totalBoxX + 4, totalY + 8.5);
  doc.setFontSize(12);
  doc.text(formatCurrency(totals.total), pageWidth - margin - 4, totalY + 8.5, {
    align: 'right',
  });

  yPos = Math.max(blockTop + obsHeight, totalY + 13) + 8;

  // === 5. DIAGNÓSTICO / PAGAMENTO / PIX (compartilhadas; o retorno avança o
  // yPos — jsPDF não tem layout, armadilha #13) ===
  yPos = renderServiceOrderSections(doc, data, yPos, {
    margin,
    pageWidth,
    pageHeight,
    grayText,
    darkText,
  });

  yPos = renderDiagnosisSection(doc, data, yPos, {
    margin,
    pageWidth,
    pageHeight,
    grayText,
    darkText,
  });

  // Campos extras do prestador: 0 de avanço quando não há (#L5).
  yPos = renderExtraFields(doc, data, yPos, {
    margin,
    pageWidth,
    pageHeight,
    grayText,
    darkText,
  });
  yPos = renderPaymentSection(doc, data, yPos, {
    margin,
    pageWidth,
    pageHeight,
    grayText,
    darkText,
  });

  yPos = await renderPixSection(doc, data, yPos, {
    primaryColor,
    grayText,
    darkText,
    margin,
    pageWidth,
    pageHeight,
  });

  // === 6. TERMOS ===
  if (data.termsConditions) {
    const lines = doc.splitTextToSize(data.termsConditions, contentWidth - 10);
    const termsHeight = Math.max(20, lines.length * 3.8 + 13);
    if (yPos + termsHeight > pageHeight - 30) {
      doc.addPage();
      yPos = 20;
    }
    doc.setFillColor(...GRAY_BOX);
    doc.roundedRect(margin, yPos, contentWidth, termsHeight, 2, 2, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.setTextColor(...GRAY_DARK);
    doc.text('Termos e condições', margin + 4, yPos + 6);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(...grayText);
    doc.text(lines, margin + 4, yPos + 11.5);
    yPos += termsHeight + 7;
  }

  // === 7. FOTOS ===
  yPos = await renderPhotosSection(doc, data, yPos, {
    darkText,
    grayText,
    grayLight,
    margin,
    pageWidth,
    pageHeight,
  });

  // === 8. ASSINATURAS ===
  await renderSignatureSection(doc, data, {
    darkText,
    grayText,
    margin,
    pageWidth,
    pageHeight,
    contentBottom: yPos,
  });

  // === 9. RODAPÉ ===
  renderFooter(doc, data, {
    primaryColor,
    grayText,
    margin,
    pageWidth,
    pageHeight,
  });
}
